import type { Day, FrequencyType, QuoteInfo, Reader, ScheduledRunOccurrence } from 'apiConnector/scheduledRun/model';
import type {
  EditRunResponse,
  Run,
  ValidateQuotesRequest,
  ValidateQuotesResult,
} from 'apiConnector/scheduledRun/scheduledRuns';
import type { ConfirmBannerType, ScheduledRun, ScheduleOperation, TriggerMode } from 'state/scheduledRuns/state';
import type { TypedError } from 'utils/errors/errors';

type ScheduledRunActionsType = typeof scheduledRunActions;
export type ScheduledRunActions = ReturnType<ScheduledRunActionsType[keyof ScheduledRunActionsType]>;

export const scheduledRunActions = {
  init: (id: string, run: ScheduledRun) => ({
    type: 'SCHEDULED_RUN_INIT' as const,
    id,
    run,
  }),
  resetScheduleType: (id: string) => ({
    type: 'SCHEDULED_RUN_RESET_TYPE' as const,
    id,
  }),
  triggerModeChanged: (id: string, triggerMode: TriggerMode) => ({
    type: 'SCHEDULED_RUN_TRIGGER_MODE_CHANGED' as const,
    id,
    triggerMode,
  }),
  daysChanged: (id: string, days?: readonly Day[]) => ({
    type: 'SCHEDULED_RUN_DAYS_CHANGED' as const,
    id,
    days,
  }),
  titleChanged: (id: string, title?: string) => ({
    type: 'SCHEDULED_RUN_TITLE_CHANGED' as const,
    id,
    title,
  }),
  commentChanged: (id: string, comment?: string) => ({
    type: 'SCHEDULED_RUN_COMMENT_CHANGED' as const,
    id,
    comment,
  }),
  timeChanged: (id: string, time?: string) => ({
    type: 'SCHEDULED_RUN_TIME_CHANGED' as const,
    id,
    time,
  }),
  datesRangeChanged: (id: string, startDate?: string, endDate?: string) => ({
    type: 'SCHEDULED_RUN_DATES_RANGE_CHANGED' as const,
    id,
    startDate,
    endDate,
  }),
  frequencyChanged: (id: string, frequency: FrequencyType) => ({
    type: 'SCHEDULED_RUN_FREQUENCY_CHANGED' as const,
    id,
    frequency,
  }),
  frequencyTimeChanged: (id: string, frequencyTime?: number) => ({
    type: 'SCHEDULED_RUN_FREQUENCY_TIME_CHANGED' as const,
    id,
    frequencyTime,
  }),
  editSchedule: (id: string, quotes: readonly QuoteInfo[]) => ({
    type: 'SCHEDULED_RUN_NEW_SCHEDULE' as const,
    id,
    quotes,
  }),
  selectRun: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_SELECTED_RUN' as const,
    runId,
    id,
  }),
  setOperation: (id: string, operation: ScheduleOperation | undefined) => ({
    type: 'SCHEDULED_RUN_SET_OPERATION' as const,
    operation,
    id,
  }),
  setQuotes: (id: string, quotes: QuoteInfo[]) => ({
    type: 'SCHEDULED_RUN_SET_QUOTES' as const,
    quotes,
    id,
  }),
  updateQuotes: (id: string, quotes: QuoteInfo[]) => ({
    type: 'SCHEDULED_RUN_ADD_TO_SCHEDULE' as const,
    quotes,
    id,
  }),
  pauseScheduleRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_PAUSE_SCHEDULE_REQUESTED' as const,
    id,
  }),
  pauseScheduleSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_PAUSE_SCHEDULE_SUCCEEDED' as const,
    id,
    runId,
  }),
  pauseScheduleFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_PAUSE_SCHEDULE_FAILED' as const,
    id,
    error,
  }),
  rescheduleRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_RESCHEDULE_REQUESTED' as const,
    id,
  }),
  rescheduleSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_RESCHEDULE_SUCCEEDED' as const,
    id,
    runId,
  }),
  rescheduleFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_RESCHEDULE_FAILED' as const,
    id,
    error,
  }),
  deleteScheduleRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_DELETE_SCHEDULE_REQUESTED' as const,
    id,
  }),
  deleteScheduleSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_DELETE_SCHEDULE_SUCCEEDED' as const,
    id,
    runId,
  }),
  deleteScheduleFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_DELETE_SCHEDULE_FAILED' as const,
    id,
    error,
  }),

  updateScheduleRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_UPDATE_SCHEDULE_REQUESTED' as const,
    id,
  }),
  updateScheduleSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_UPDATE_SCHEDULE_SUCCEEDED' as const,
    id,
    runId,
  }),
  updateScheduleFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_UPDATE_SCHEDULE_FAILED' as const,
    id,
    error,
  }),
  addToScheduleRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_ADD_TO_SCHEDULE_REQUESTED' as const,
    id,
  }),
  addToScheduleSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_ADD_TO_SCHEDULE_SUCCEEDED' as const,
    id,
    runId,
  }),
  addToScheduleFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_ADD_TO_SCHEDULE_FAILED' as const,
    id,
    error,
  }),
  updateQuotesRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_UPDATE_QUOTES_REQUESTED' as const,
    id,
  }),
  updateQuotesSucceeded: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_UPDATE_QUOTES_SUCCEEDED' as const,
    id,
    runId,
  }),
  updateQuotesFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_UPDATE_QUOTES_FAILED' as const,
    id,
    error,
  }),
  quoteIdsChanged: (id: string, quoteIds: readonly string[]) => ({
    type: 'SCHEDULED_RUN_QUOTE_IDS_CHANGED' as const,
    id,
    quoteIds,
  }),
  readersChanged: <T extends Reader>(id: string, readers: readonly T[]) => ({
    type: 'SCHEDULED_RUN_READERS_CHANGED' as const,
    id,
    readers,
  }),
  scheduledRunCreationRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_CREATION_REQUESTED' as const,
    id,
  }),
  scheduledRunCreationSucceeded: (id: string, createResponse: EditRunResponse) => ({
    type: 'SCHEDULED_RUN_CREATION_SUCCEEDED' as const,
    id,
    createResponse,
  }),
  scheduledRunCreationFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_CREATION_FAILED' as const,
    id,
    error,
  }),
  scheduledRunNameChecked: (id: string, existingTitle?: string) => ({
    type: 'SCHEDULED_RUN_NAME_CHECKED' as const,
    id,
    existingTitle,
  }),
  scheduledRunNameCheckFailed: (id: string, error?: string) => ({
    type: 'SCHEDULED_RUN_CHECK_NAME_FAILED' as const,
    id,
    error,
  }),
  scheduledRunRunsRequested: (id: string) => ({
    type: 'SCHEDULED_RUN_RUNS_REQUESTED' as const,
    id,
  }),
  scheduledRunRunsReceived: (id: string, runs: Run[]) => ({
    type: 'SCHEDULED_RUN_RUNS_RECEIVED' as const,
    id,
    runs,
  }),
  scheduledRunRunsFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_RUNS_FAILED' as const,
    id,
    error,
  }),
  resetRunRequested: (id: string, runId: string) => ({
    type: 'SCHEDULED_RUN_RESET_RUN_REQUESTED' as const,
    id,
    runId,
  }),
  setConfirmBanner: (id: string, confirmBanner?: ConfirmBannerType) => ({
    type: 'SCHEDULED_RUN_SET_CONFIRM_BANNER' as const,
    id,
    confirmBanner,
  }),
  validateQuoteToAdd: (id: string, validateRequest: ValidateQuotesRequest) => ({
    type: 'SCHEDULED_RUN_VALIDATE_QUOTES' as const,
    id,
    validateRequest,
  }),
  validateQuotesSucceeded: (id: string, validationResult: ValidateQuotesResult) => ({
    type: 'SCHEDULED_RUN_VALIDATE_QUOTES_SUCCEEDED' as const,
    id,
    validationResult,
  }),
  validateQuotesFailed: (id: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_VALIDATE_QUOTES_FAILED' as const,
    id,
    error,
  }),
  occurrencesRequested: (id: string, runId: string, requestId: string) => ({
    type: 'SCHEDULED_RUN_OCCURRENCES_REQUESTED' as const,
    id,
    runId,
    requestId,
  }),
  occurrencesReceived: (
    id: string,
    runId: string,
    requestId: string,
    occurrences: readonly ScheduledRunOccurrence[],
  ) => ({
    type: 'SCHEDULED_RUN_OCCURRENCES_RECEIVED' as const,
    id,
    runId,
    requestId,
    occurrences,
  }),
  occurrencesFailed: (id: string, runId: string, requestId: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_OCCURRENCES_FAILED' as const,
    id,
    runId,
    requestId,
    error,
  }),
  occurrenceDetailRequested: (id: string, runId: string, occurrenceId: string, requestId: string) => ({
    type: 'SCHEDULED_RUN_OCCURRENCE_DETAIL_REQUESTED' as const,
    id,
    runId,
    occurrenceId,
    requestId,
  }),
  occurrenceDetailReceived: (
    id: string,
    runId: string,
    occurrenceId: string,
    requestId: string,
    occurrence: ScheduledRunOccurrence,
  ) => ({
    type: 'SCHEDULED_RUN_OCCURRENCE_DETAIL_RECEIVED' as const,
    id,
    runId,
    occurrenceId,
    requestId,
    occurrence,
  }),
  occurrenceDetailFailed: (id: string, runId: string, occurrenceId: string, requestId: string, error: TypedError) => ({
    type: 'SCHEDULED_RUN_OCCURRENCE_DETAIL_FAILED' as const,
    id,
    runId,
    occurrenceId,
    requestId,
    error,
  }),
  occurrenceSelected: (id: string, occurrenceId?: string) => ({
    type: 'SCHEDULED_RUN_OCCURRENCE_SELECTED' as const,
    id,
    occurrenceId,
  }),
  occurrenceReloadStarted: (
    id: string,
    runId: string,
    operationId: string,
    tabIds: string | readonly string[],
    queuedQuoteIds: readonly string[],
    skippedQuoteIds: readonly string[],
    skippedCount: number,
    queuedQuoteIdsByTabId?: Readonly<Record<string, readonly string[]>>,
  ) => {
    const normalizedTabIds = typeof tabIds === 'string' ? [tabIds] : tabIds;
    return {
      type: 'SCHEDULED_RUN_OCCURRENCE_RELOAD_STARTED' as const,
      id,
      runId,
      operationId,
      tabIds: normalizedTabIds,
      queuedQuoteIds,
      queuedQuoteIdsByTabId:
        queuedQuoteIdsByTabId ?? (normalizedTabIds.length === 1 ? { [normalizedTabIds[0]]: queuedQuoteIds } : {}),
      skippedQuoteIds,
      skippedCount,
    };
  },
  occurrenceReloadReset: (id: string, operationId?: string) => ({
    type: 'SCHEDULED_RUN_OCCURRENCE_RELOAD_RESET' as const,
    id,
    ...(operationId === undefined ? {} : { operationId }),
  }),
};
